import { useState, useEffect } from 'react';
import { usePrivacy } from '../context/PrivacyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Euro, TrendingUp, Clock, AlertTriangle, Percent, Landmark, BarChart3 } from 'lucide-react';
import BankTransacties from '../components/BankTransacties';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../lib/api';
import { toast } from 'sonner';

const MAAND_LABELS = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
const TYPE_COLORS = ['#f97316', '#22c55e'];

const fmtEUR = (v) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);

export default function Financieel() {
  const { privacy } = usePrivacy();
  const fmt = (v) => privacy ? '€ \u2022\u2022\u2022\u2022' : fmtEUR(v);
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overzicht');

  const fetchStats = async (y) => {
    setLoading(true);
    try {
      const res = await api.get(`/financieel/stats?year=${y}`);
      setStats(res.data);
    } catch (err) {
      toast.error('Kon financiële cijfers niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(year); }, [year]);

  if (loading && !stats) {
    return <div className="p-8 text-muted-foreground">Cijfers laden...</div>;
  }
  if (!stats) return null;

  const { kpi, per_maand, per_kwartaal, per_type, top_klanten, conversie } = stats;
  const maandData = per_maand.map(m => ({ ...m, naam: MAAND_LABELS[m.maand - 1] }));
  const maandVerschil = kpi.deze_maand !== null && kpi.vorige_maand !== null && kpi.vorige_maand > 0
    ? Math.round(((kpi.deze_maand - kpi.vorige_maand) / kpi.vorige_maand) * 100)
    : null;

  return (
    <div className="space-y-6" data-testid="financieel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Financieel</h1>
          <p className="text-muted-foreground">Omzet en cijfers — alle facturen (reiniging + installatie)</p>
        </div>
        {tab === 'overzicht' && (
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="financieel-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(stats.available_years?.length ? stats.available_years : [String(year)]).map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('overzicht')} data-testid="financieel-tab-overzicht"
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${tab === 'overzicht' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
          <BarChart3 className="w-4 h-4" /> Overzicht
        </button>
        <button onClick={() => setTab('bank')} data-testid="financieel-tab-bank"
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${tab === 'bank' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
          <Landmark className="w-4 h-4" /> Bank
        </button>
      </div>

      {tab === 'bank' ? <BankTransacties /> : (<>
      {/* KPI tegels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="kpi-gefactureerd">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Euro className="w-4 h-4" /> Gefactureerd {stats.year}</div>
            <p className="text-2xl font-bold mt-1">{fmt(kpi.gefactureerd_incl)}</p>
            <p className="text-xs text-muted-foreground">{fmt(kpi.gefactureerd_excl)} excl. BTW · {kpi.aantal_facturen} facturen</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-betaald">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><TrendingUp className="w-4 h-4" /> Betaald {stats.year}</div>
            <p className="text-2xl font-bold mt-1 text-green-600">{fmt(kpi.betaald_incl)}</p>
            {kpi.deze_maand !== null && (
              <p className="text-xs text-muted-foreground">
                Deze maand gefactureerd: {fmt(kpi.deze_maand)}
                {maandVerschil !== null && (
                  <span className={maandVerschil >= 0 ? 'text-green-600' : 'text-red-500'}> ({maandVerschil >= 0 ? '+' : ''}{maandVerschil}% t.o.v. vorige)</span>
                )}
              </p>
            )}
          </CardContent>
        </Card>
        <Card data-testid="kpi-openstaand">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Clock className="w-4 h-4" /> Openstaand</div>
            <p className="text-2xl font-bold mt-1 text-orange-500">{fmt(kpi.openstaand)}</p>
            <p className="text-xs text-muted-foreground">{kpi.openstaand_aantal} verstuurde, onbetaalde facturen</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-achterstallig">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><AlertTriangle className="w-4 h-4" /> Achterstallig</div>
            <p className="text-2xl font-bold mt-1 text-red-500">{fmt(kpi.achterstallig)}</p>
            <p className="text-xs text-muted-foreground">{kpi.achterstallig_aantal} facturen over vervaldatum</p>
          </CardContent>
        </Card>
      </div>

      {/* Grafieken */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-maand">
          <CardHeader><CardTitle className="text-base">Omzet per maand</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={maandData}>
                <XAxis dataKey="naam" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => privacy ? '\u2022\u2022' : `\u20ac${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, name) => [fmt(v), name === 'gefactureerd' ? 'Gefactureerd' : 'Betaald']} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Bar dataKey="gefactureerd" fill="#f97316" radius={[4, 4, 0, 0]} />
                <Bar dataKey="betaald" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card data-testid="chart-type">
          <CardHeader><CardTitle className="text-base">Reiniging vs. Installatie</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={per_type} dataKey="gefactureerd" nameKey="type" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {per_type.map((_, i) => <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {per_type.map((t, i) => (
                <div key={t.type} className="flex justify-between text-sm">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: TYPE_COLORS[i] }} />{t.type} ({t.aantal})</span>
                  <span className="font-medium">{fmt(t.gefactureerd)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kwartalen + top klanten + conversie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid="kwartaal-tabel">
          <CardHeader><CardTitle className="text-base">Per kwartaal (BTW-aangifte)</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-muted-foreground">
                <tr className="text-left"><th className="pb-2">Kwartaal</th><th className="pb-2 text-right">Excl. BTW</th><th className="pb-2 text-right">BTW</th><th className="pb-2 text-right">Incl.</th></tr>
              </thead>
              <tbody>
                {per_kwartaal.map(q => (
                  <tr key={q.kwartaal} className="border-t border-border/50">
                    <td className="py-2">{q.kwartaal} <span className="text-xs text-muted-foreground">({q.aantal})</span></td>
                    <td className="py-2 text-right">{fmt(q.gefactureerd_excl)}</td>
                    <td className="py-2 text-right text-muted-foreground">{fmt(q.btw)}</td>
                    <td className="py-2 text-right font-medium">{fmt(q.gefactureerd_incl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <Card data-testid="top-klanten">
          <CardHeader><CardTitle className="text-base">Top 5 klanten</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {top_klanten.length === 0 && <p className="text-sm text-muted-foreground">Nog geen facturen dit jaar.</p>}
            {top_klanten.map((k, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="w-6 justify-center flex-shrink-0">{i + 1}</Badge>
                  <span className="truncate">{k.customer_name}</span>
                </span>
                <span className="font-medium flex-shrink-0">{fmt(k.totaal)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card data-testid="conversie">
          <CardHeader><CardTitle className="text-base">Offerte-conversie {stats.year}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Percent className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">{conversie.percentage}%</p>
                <p className="text-xs text-muted-foreground">{conversie.geaccepteerd} van {conversie.totaal} offertes getekend</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </>)}
    </div>
  );
}
